import {useState, useRef, useEffect, useCallback, useMemo} from "@wordpress/element";
import {useSelect, useDispatch} from '@wordpress/data';
import {CART_STORE_KEY} from '@woocommerce/block-data';
import {getSetting} from '@woocommerce/settings';
import {isEmail} from '@wordpress/url';
import {useLoadPayPalScript} from "../../hooks";
import {useCreateFastlane, useProcessFastlane} from "../../payment-methods/fastlane/hooks";

export const Block = ({checkoutExtensionData, extensions}) => {
    const isInitialRender = useRef(true);
    const currentData = useRef({});
    const queryParams = getSetting('paypalQueryParams');
    const data = extensions?.wc_ppcp?.fastlane || {
        i18n: {},
        fastlane_pageload: false,
        emailDetectionEnabled: false,
        iconEnabled: false
    };

    const {
        needsShipping,
        billingAddress
    } = useSelect(select => {
        const store = select(CART_STORE_KEY);

        return {
            needsShipping: store.getNeedsShipping(),
            ...store.getCustomerData()
        }
    });

    const {
        setShippingAddress
    } = useDispatch(CART_STORE_KEY);

    const {
        email
    } = billingAddress;

    const [debouncedEmail, setDebouncedEmail] = useState(email);

    const paypal = useLoadPayPalScript(queryParams);

    const fastlane = useCreateFastlane({paypal});

    const onError = useCallback(() => {

    }, []);

    const shippingData = useMemo(() => {
        return {
            needsShipping,
            setShippingAddress
        }
    }, [needsShipping, setShippingAddress]);

    const {
        emails,
        isCustomerFound,
        triggerAuthentication
    } = useProcessFastlane({
        i18n: data.i18n,
        email,
        onError,
        fastlane,
        shippingData,
        openFastlaneModal: () => null
    });

    useEffect(() => {
        currentData.current = {fastlane, emails, isCustomerFound};
    }, [emails, fastlane, isCustomerFound]);

    useEffect(() => {
        // Set a timer to update the debounced value after 500ms
        const timer = setTimeout(() => {
            setDebouncedEmail(email);
        }, 500); // Adjust debounce time as needed (500ms is common)

        // Clean up the timer if email changes before timeout completes
        return () => clearTimeout(timer);
    }, [email]);

    // Effect for handling email changes
    useEffect(() => {
        const {
            emails,
            isCustomerFound
        } = currentData.current;

        // If customer cancelled, don't try to authenticate.
        if (emails.includes(debouncedEmail) || isCustomerFound) {
            return;
        }
        // Don't do anything if requirements aren't met
        if (!fastlane || !triggerAuthentication || !isEmail(debouncedEmail)) {
            return;
        }

        // Only trigger on first load if fastlane_pageload is true
        if (isInitialRender.current) {
            isInitialRender.current = false;
            if (data.fastlane_pageload) {
                setTimeout(() => triggerAuthentication(debouncedEmail, false), 1000);
            }
            return;
        }

        if (data.emailDetectionEnabled) {
            // If we get here, email has changed and is valid, so trigger authentication
            triggerAuthentication(debouncedEmail, false);
        }
    }, [fastlane, debouncedEmail, triggerAuthentication]);

    useEffect(() => {
        const {
            fastlane
        } = currentData.current;

        if (!fastlane || !data.iconEnabled) {
            return;
        }
        let el = document.getElementById('email');
        if (el) {
            let container = document.getElementById('wc-ppcp-watermark-container');
            if (container) {
                return;
            }
            container = document.createElement('div');
            container.id = 'wc-ppcp-watermark-container';

            fastlane.FastlaneWatermarkComponent({includeAdditionalInfo: true}).then(component => {
                el.parentElement.append(container);
                component.render('#wc-ppcp-watermark-container');
            });
        }
    });

    return null;
}